// ============================================================
// Console Super Admin — Présence des utilisateurs (en ligne / hors ligne)
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Search, RefreshCw, Users, Wifi } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';

const ROLE_LABELS = {
  admin: 'Administrateur', proprietaire: 'Propriétaire',
  gerant_vendeur: 'Employé', vendeur: 'Vendeur', consultation: 'Consultation',
};

function tempsRelatif(date) {
  if (!date) return null;
  const d = new Date(date);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function SuperAdminPresence() {
  const [data, setData] = useState({ utilisateurs: [], resume: { total: 0, en_ligne: 0 } });
  const [commerces, setCommerces] = useState([]);
  const [q, setQ] = useState('');
  const [commerceId, setCommerceId] = useState('');
  const [statut, setStatut] = useState('');
  const [loading, setLoading] = useState(true);

  // Liste des commerces (filtre)
  useEffect(() => {
    superAdminApi.get('/superadmin/commerces')
      .then((r) => setCommerces(Array.isArray(r.data) ? r.data : (r.data.commerces || [])))
      .catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (commerceId) params.commerce_id = commerceId;
      if (statut) params.statut = statut;
      const { data } = await superAdminApi.get('/superadmin/commerces/presence', { params });
      setData(data);
    } catch {
      /* silencieux */
    } finally {
      setLoading(false);
    }
  }, [q, commerceId, statut]);

  // Chargement + rafraîchissement automatique toutes les 30 s
  useEffect(() => {
    const t = setTimeout(charger, 250);          // debounce des filtres
    return () => clearTimeout(t);
  }, [charger]);

  useEffect(() => {
    const id = setInterval(charger, 30000);
    return () => clearInterval(id);
  }, [charger]);

  const { utilisateurs, resume } = data;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Activity size={20} className="text-blue-600" /> Présence des utilisateurs
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            « En ligne » = activité dans les 5 dernières minutes. Actualisé automatiquement.
          </p>
        </div>
        <button onClick={charger}
          className="inline-flex items-center gap-2 text-sm bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-emerald-600"><Wifi size={16} />
            <span className="text-xs text-slate-400">En ligne maintenant</span></div>
          <div className="text-3xl font-bold text-emerald-600 mt-1">{resume.en_ligne}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-slate-500"><Users size={16} />
            <span className="text-xs text-slate-400">Comptes actifs (total)</span></div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{resume.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <span className="text-xs text-slate-400">Affichés (filtre)</span>
          <div className="text-3xl font-bold text-slate-900 mt-1">{utilisateurs.length}</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher — nom, prénom, email…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-blue-400" />
        </div>
        <select value={commerceId} onChange={(e) => setCommerceId(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-400">
          <option value="">Tous les commerces</option>
          {commerces.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select value={statut} onChange={(e) => setStatut(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-400">
          <option value="">Tous les statuts</option>
          <option value="en_ligne">En ligne</option>
          <option value="hors_ligne">Hors ligne</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                <th className="text-left px-3 py-2">Statut</th>
                <th className="text-left px-3 py-2">Utilisateur</th>
                <th className="text-left px-3 py-2">Commerce</th>
                <th className="text-left px-3 py-2">Rôle</th>
                <th className="text-left px-3 py-2">Dernière activité</th>
                <th className="text-left px-3 py-2">Dernière connexion</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Chargement…</td></tr>
              ) : utilisateurs.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Aucun utilisateur.</td></tr>
              ) : (
                utilisateurs.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <span className={`w-2.5 h-2.5 rounded-full ${u.en_ligne ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className={u.en_ligne ? 'text-emerald-700 font-medium' : 'text-slate-400'}>
                          {u.en_ligne ? 'En ligne' : 'Hors ligne'}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{u.prenom} {u.nom}</div>
                      <div className="text-xs text-slate-400">{u.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/superadmin/commerces/${u.commerce_id}`} className="text-blue-600 hover:underline">
                        {u.commerce_nom}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{ROLE_LABELS[u.role] || u.role}</td>
                    <td className="px-3 py-2 text-slate-500">{tempsRelatif(u.derniere_activite) || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-500">{tempsRelatif(u.derniere_connexion) || <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
